import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { AlertTriangle, TrendingUp, Users, Calendar, Activity, Award, ChartBar, Package, FileText, FileSpreadsheet } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../../api';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

const alertColor = (level) => ({
  normal: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  warning: 'bg-amber-100 text-amber-800 border-amber-200',
  high: 'bg-orange-100 text-orange-800 border-orange-200',
  limit: 'bg-red-100 text-red-800 border-red-200',
}[level] || 'bg-slate-100 text-slate-700 border-slate-200');

const alertBar = (level) => ({
  normal: 'bg-emerald-500',
  warning: 'bg-amber-500',
  high: 'bg-orange-500',
  limit: 'bg-red-600',
}[level] || 'bg-slate-500');

function ResourceCard({ title, icon: Icon, bucket, subtitle }) {
  if (!bucket) return null;
  const unlim = bucket.is_unlimited;
  const level = bucket.alert_level || 'normal';
  const noConf = bucket.limit_source === 'none';
  return (
    <Card className="border-slate-200" data-testid={`resource-card-${title.toLowerCase()}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            {Icon && <Icon className="w-4 h-4" />}
            <span>{title}</span>
          </div>
          {!unlim && <Badge className={`text-[10px] border ${alertColor(level)}`}>{bucket.percentage}%</Badge>}
          {unlim && <Badge className="text-[10px] bg-sky-100 text-sky-700 border border-sky-200">{noConf ? 'No configurado' : 'Ilimitado'}</Badge>}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {unlim ? (
          <div>
            <p className="text-2xl font-bold text-slate-800">{bucket.used}</p>
            <p className="text-xs text-slate-500 mt-1">{noConf ? 'Este plan no define un tope' : 'Sin límite contratado'}</p>
          </div>
        ) : (
          <>
            <div className="flex items-end justify-between mb-2">
              <p className="text-2xl font-bold text-slate-800">
                {bucket.used}
                <span className="text-sm font-normal text-slate-500"> / {bucket.limit}</span>
              </p>
              <p className="text-xs text-slate-500">
                {bucket.remaining} disponibles
              </p>
            </div>
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${alertBar(level)} transition-all`}
                   style={{ width: `${Math.min(100, bucket.percentage)}%` }} />
            </div>
            {bucket.over_limit && (
              <p className="text-[11px] text-red-700 mt-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Límite excedido — bloquea nuevas altas
              </p>
            )}
            {typeof bucket.used_establishment === 'number' && bucket.used_establishment !== bucket.used && (
              <p className="text-[11px] text-slate-500 mt-2">
                En este establecimiento: <b>{bucket.used_establishment}</b>
              </p>
            )}
          </>
        )}
        {subtitle && <p className="text-[11px] text-slate-400 mt-2 italic">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}

function PlanSummaryCard({ plan, establishment }) {
  if (!plan) return null;
  const badgeCls = plan.status === 'activa' ? 'bg-emerald-100 text-emerald-700' :
                   plan.status === 'prueba' ? 'bg-amber-100 text-amber-700' :
                   'bg-slate-100 text-slate-700';
  return (
    <Card className="border-slate-200">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">Plan actual</p>
            <p className="text-2xl font-bold text-slate-800 mt-1">{plan.name || '—'}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={badgeCls}>{plan.status}</Badge>
              <Badge variant="outline" className="text-slate-600">{plan.modalidad}</Badge>
            </div>
          </div>
          <Award className="w-8 h-8 text-slate-300" />
        </div>
        <div className="grid grid-cols-2 gap-4 text-xs mt-4">
          <div>
            <p className="text-slate-500">Inicio</p>
            <p className="font-medium text-slate-700">{plan.period_start ? new Date(plan.period_start).toLocaleDateString('es-CL') : '—'}</p>
          </div>
          <div>
            <p className="text-slate-500">Vencimiento</p>
            <p className="font-medium text-slate-700">
              {plan.period_end ? new Date(plan.period_end).toLocaleDateString('es-CL') : '—'}
              {typeof plan.days_remaining === 'number' && plan.days_remaining >= 0 && (
                <span className="ml-2 text-[10px] text-slate-500">({plan.days_remaining} días)</span>
              )}
            </p>
          </div>
          {establishment && (
            <div className="col-span-2">
              <p className="text-slate-500">Establecimiento</p>
              <p className="font-medium text-slate-700">{establishment.nombre}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const [establishmentId, setEstablishmentId] = useState(user?.establishment_id || null);
  const [establishments, setEstablishments] = useState([]);
  const [usage, setUsage] = useState(null);
  const [byDate, setByDate] = useState([]);
  const [byStudent, setByStudent] = useState([]);
  const [byScenario, setByScenario] = useState([]);
  const [usersDetail, setUsersDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rangeDays, setRangeDays] = useState(30);

  // Cargar lista de establishments para selector (super_admin y admin)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/establishments');
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.establishments || []);
        setEstablishments(list);
        if (!establishmentId && list.length > 0) setEstablishmentId(list[0].id);
      } catch (_) { /* no-op */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = useCallback(async (estId) => {
    if (!estId) return;
    setLoading(true);
    try {
      const [u, byd, bys, bysc, ud] = await Promise.all([
        api.get(`/reports/establishment/${estId}/subscription-usage`),
        api.get(`/reports/establishment/${estId}/simulations-by-date?granularity=day&days=${rangeDays}`),
        api.get(`/reports/establishment/${estId}/simulations-by-student?limit=20`),
        api.get(`/reports/establishment/${estId}/simulations-by-scenario?limit=20`),
        api.get(`/reports/establishment/${estId}/users`),
      ]);
      setUsage(u.data);
      setByDate(byd.data?.series || []);
      setByStudent(bys.data?.rows || []);
      setByScenario(bysc.data?.rows || []);
      setUsersDetail(ud.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error cargando reportes');
    } finally { setLoading(false); }
  }, [rangeDays]);

  useEffect(() => { if (establishmentId) loadAll(establishmentId); }, [establishmentId, loadAll]);

  const download = async (format) => {
    if (!establishmentId) return;
    try {
      const r = await api.get(`/reports/establishment/${establishmentId}/export/${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const est = usage?.establishment?.nombre || 'establecimiento';
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `reporte_${est.replace(/\s+/g, '_')}_${ts}.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Error exportando ${format.toUpperCase()}`);
    }
  };

  const { plan, users, simulations, establishment } = usage || {};

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ChartBar className="w-6 h-6 text-cyan-600" />
            Reportes por Establecimiento
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Consumo del plan, actividad académica y disponibilidad.
          </p>
        </div>
        {establishments.length > 1 && (
          <select
            data-testid="establishment-selector"
            value={establishmentId || ''}
            onChange={(e) => setEstablishmentId(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {establishments.map((e) => (
              <option key={e.id} value={e.id}>{e.nombre || e.name || e.id}</option>
            ))}
          </select>
        )}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => download('pdf')}
                  disabled={!establishmentId || loading || !usage || usage.error}
                  data-testid="export-pdf-btn"
                  className="border-red-300 text-red-700 hover:bg-red-50">
            <FileText className="w-4 h-4 mr-1" /> PDF ejecutivo
          </Button>
          <Button variant="outline" size="sm" onClick={() => download('xlsx')}
                  disabled={!establishmentId || loading || !usage || usage.error}
                  data-testid="export-xlsx-btn"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-500 py-6 text-center">Cargando…</p>}

      {!loading && usage?.error && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-6 text-sm text-amber-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {usage.error}
          </CardContent>
        </Card>
      )}

      {!loading && !usage?.error && usage && (
        <Tabs defaultValue="resumen" className="space-y-4">
          <TabsList data-testid="reports-tabs">
            <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="plan" data-testid="tab-plan">Uso del Plan</TabsTrigger>
            <TabsTrigger value="simulaciones" data-testid="tab-simulaciones">Simulaciones</TabsTrigger>
            <TabsTrigger value="usuarios" data-testid="tab-usuarios">Usuarios</TabsTrigger>
          </TabsList>

          {/* ==== RESUMEN ==== */}
          <TabsContent value="resumen" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <PlanSummaryCard plan={plan} establishment={establishment} />
              <ResourceCard title="Simulaciones" icon={Activity} bucket={simulations}
                            subtitle={`Período: ${simulations?.period_label || 'mes actual'}`} />
              <ResourceCard title="Total Usuarios" icon={Users} bucket={users?.total} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ResourceCard title="Alumnos" icon={Users} bucket={users?.students} />
              <ResourceCard title="Instructores" icon={Users} bucket={users?.instructors} />
              <ResourceCard title="Administradores" icon={Users} bucket={users?.admins} />
            </div>
          </TabsContent>

          {/* ==== USO DEL PLAN ==== */}
          <TabsContent value="plan" className="space-y-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Detalle de límites y consumo</CardTitle></CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b text-slate-500">
                      <th className="py-2">Recurso</th>
                      <th className="py-2">Origen</th>
                      <th className="py-2 text-right">Límite</th>
                      <th className="py-2 text-right">Usado</th>
                      <th className="py-2 text-right">Disponible</th>
                      <th className="py-2 text-right">% uso</th>
                      <th className="py-2 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Alumnos', users?.students],
                      ['Instructores', users?.instructors],
                      ['Administradores', users?.admins],
                      ['Total usuarios', users?.total],
                      ['Simulaciones', simulations],
                    ].filter(([, b]) => !!b).map(([label, b]) => (
                      <tr key={label} className="border-b last:border-b-0" data-testid={`plan-row-${label.toLowerCase().replace(/\s+/g,'-')}`}>
                        <td className="py-2 font-medium text-slate-700">{label}</td>
                        <td className="py-2 text-xs text-slate-500">
                          {b.limit_source === 'override' ? 'Excepción' :
                           b.limit_source === 'plan' ? 'Plan' :
                           b.limit_source === 'none' ? '—' : b.limit_source}
                        </td>
                        <td className="py-2 text-right">{b.is_unlimited ? '∞' : b.limit}</td>
                        <td className="py-2 text-right font-medium">{b.used}</td>
                        <td className="py-2 text-right">{b.is_unlimited ? '∞' : b.remaining}</td>
                        <td className="py-2 text-right">{b.is_unlimited ? '—' : `${b.percentage}%`}</td>
                        <td className="py-2 text-center">
                          <Badge className={`text-[10px] border ${alertColor(b.alert_level)}`}>
                            {b.alert_level === 'normal' ? 'OK' :
                             b.alert_level === 'warning' ? 'Alerta' :
                             b.alert_level === 'high' ? 'Alto' : 'Límite'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[11px] text-slate-400 mt-4">
                  Umbrales: normal &lt; {usage.thresholds?.warning}% · alerta ≥ {usage.thresholds?.warning}% · alto ≥ {usage.thresholds?.high}% · límite ≥ {usage.thresholds?.limit}%
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ==== SIMULACIONES ==== */}
          <TabsContent value="simulaciones" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4" />Simulaciones por fecha
                  </CardTitle>
                  <select
                    value={rangeDays}
                    onChange={(e) => setRangeDays(parseInt(e.target.value))}
                    className="border border-slate-300 rounded-lg px-2 py-1 text-xs bg-white"
                    data-testid="range-selector"
                  >
                    <option value="7">Últimos 7 días</option>
                    <option value="30">Últimos 30 días</option>
                    <option value="90">Últimos 90 días</option>
                    <option value="365">Último año</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {byDate.length === 0 ? (
                  <p className="text-sm text-slate-400 py-8 text-center">Sin simulaciones registradas en el período</p>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={byDate}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" />Top alumnos</CardTitle></CardHeader>
                <CardContent>
                  {byStudent.length === 0 ? (
                    <p className="text-sm text-slate-400 py-4">Sin datos</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left border-b text-slate-500">
                        <th className="py-1">Alumno</th><th className="py-1 text-right">Total</th>
                        <th className="py-1 text-right">Compl.</th><th className="py-1 text-right">Prom.</th>
                      </tr></thead>
                      <tbody>{byStudent.map((r) => (
                        <tr key={r.user_id} className="border-b last:border-b-0" data-testid={`student-row-${r.user_id}`}>
                          <td className="py-1">{r.nombre}</td>
                          <td className="py-1 text-right font-medium">{r.total}</td>
                          <td className="py-1 text-right">{r.completadas}</td>
                          <td className="py-1 text-right">{r.puntaje_promedio}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" />Top escenarios</CardTitle></CardHeader>
                <CardContent>
                  {byScenario.length === 0 ? (
                    <p className="text-sm text-slate-400 py-4">Sin datos</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left border-b text-slate-500">
                        <th className="py-1">Escenario</th><th className="py-1 text-right">Ejec.</th>
                        <th className="py-1 text-right">Alum.</th><th className="py-1 text-right">Prom.</th>
                      </tr></thead>
                      <tbody>{byScenario.map((r) => (
                        <tr key={r.scenario_id} className="border-b last:border-b-0" data-testid={`scenario-row-${r.scenario_id}`}>
                          <td className="py-1 max-w-[200px] truncate" title={r.titulo}>{r.titulo}</td>
                          <td className="py-1 text-right font-medium">{r.ejecuciones}</td>
                          <td className="py-1 text-right">{r.alumnos_unicos}</td>
                          <td className="py-1 text-right">{r.puntaje_promedio}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ==== USUARIOS ==== */}
          <TabsContent value="usuarios" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4" />Usuarios del establecimiento
                  {usersDetail && <span className="text-xs font-normal text-slate-500 ml-2">
                    {usersDetail.total} total · {usersDetail.activos} activos · {usersDetail.inactivos} inactivos
                  </span>}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!usersDetail || (usersDetail.rows || []).length === 0 ? (
                  <p className="text-sm text-slate-400 py-4">Sin usuarios registrados</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b text-slate-500">
                        <th className="py-2">Nombre</th>
                        <th className="py-2">Rol</th>
                        <th className="py-2">Estado</th>
                        <th className="py-2 text-right">Simulaciones</th>
                        <th className="py-2 text-center">Cupo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersDetail.rows.map((u) => (
                        <tr key={u.id} className="border-b last:border-b-0" data-testid={`user-row-${u.id}`}>
                          <td className="py-2 font-medium text-slate-700">
                            <div>{u.nombre}</div>
                            <div className="text-[11px] text-slate-500">{u.email}</div>
                          </td>
                          <td className="py-2 text-xs uppercase tracking-wide">{u.role}</td>
                          <td className="py-2">
                            <Badge className={u.activo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                              {u.activo ? 'Activo' : 'Inactivo'}
                            </Badge>
                          </td>
                          <td className="py-2 text-right">{u.simulaciones}</td>
                          <td className="py-2 text-center text-xs">{u.consume_cupo ? '✓' : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
